import React, {useEffect, useState} from 'react'
import PropTypes from 'prop-types'
import {Helmet} from 'react-helmet'
import ThreekitWidget from '../../components/threekit-widget'
import {getAssetUrl} from '@salesforce/pwa-kit-react-sdk/ssr/universal/utils'
import {FormattedMessage, useIntl} from 'react-intl'
import {useCurrency, useVariant} from '@salesforce/retail-react-app/app/hooks'
import {useAccessToken, useConfig, useShopperBasketsMutation} from '@salesforce/commerce-sdk-react'
import {MRT_CONTEXT_BASEPATH} from '../../ssr/constants'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {useCurrentBasket} from '@salesforce/retail-react-app/app/hooks/use-current-basket'
import useProductData from '../../hooks/use-product-data'
import {useAddToCartModalContext} from '../../hooks/use-add-to-cart-modal'
import {useLocation, Link} from 'react-router-dom'
import {BRAND_KEY} from '../../brand'
import {
    Box,
    Button,
    IconButton,
    Modal,
    ModalBody,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    Text,
    useDisclosure,
    useStyleConfig,
} from '@chakra-ui/react'
import CloseIcon from '../../components/icons/action/CloseIcon'

const OfficinaModal = ({isOpen, onClose}) => {
    const styles = useStyleConfig('ThreekitWidgetContainer')
    const {buildUrl} = useMultiSite()
    const path = buildUrl('/e/officina')

    return (
        <Modal
            {...styles?.modalContainer}
            isOpen={isOpen}
            onClose={onClose}
            blockScrollOnMount={false}
            isCentered
        >
            <ModalOverlay />
            <ModalContent {...styles?.selectorContent}>
                <ModalHeader {...styles?.selectorHeader}>
                    <Text {...styles?.headerText}>
                        <FormattedMessage
                            id="officina.modal.title"
                            defaultMessage="Close product configuration?"
                        />
                    </Text>
                    <Box {...styles?.closeIconContainer}>
                        <CloseIcon
                            data-testid="close-button"
                            {...styles?.closeIcon}
                            onClick={onClose}
                        />
                    </Box>
                </ModalHeader>
                <ModalBody data-testid="modal-body" {...styles?.modalBody}>
                    <FormattedMessage
                        id="officina.modal.description"
                        defaultMessage="If you close this page, your product configuration will not be saved."
                    />
                </ModalBody>
                <ModalFooter {...styles?.selectorFooter}>
                    <Button as={Link} to={path} variant="primary" {...styles?.closeButton}>
                        <FormattedMessage id="officina.modal.close" defaultMessage="Close" />
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    )
}

OfficinaModal.propTypes = {
    isOpen: PropTypes.bool,
    onClose: PropTypes.func,
    intl: PropTypes.object,
}

const OfficinaPage = ({product}) => {
    const intl = useIntl()
    const {currency} = useCurrency()
    const [init, setInit] = useState(false)
    const {getTokenWhenReady} = useAccessToken()
    const location = useLocation()

    const {site, locale} = useMultiSite()
    const config = useConfig()

    const {isOpen, onClose, onOpen} = useDisclosure()

    const {data: basket, isLoading, refetch} = useCurrentBasket()

    const createBasket = useShopperBasketsMutation('createBasket')

    // Hide close button element
    const styles = useStyleConfig('ThreekitWidgetContainer', {
        variant: 'hiddenCloseButton',
    })

    useEffect(() => {
        // console.debug(basket)
        /*
        Understand if the basket is already created, if not create it
        TODO: Double check if the baket is the correct one 
        */
        if (!isLoading && !basket)
            createBasket.mutateAsync({
                body: {},
            })
    }, [basket, isLoading])

    const {priceData} = useProductData(product)
    const price = priceData.currentPrice

    const {
        isOpen: isAddToCartModalOpen,
        onOpen: onAddToCartModalOpen,
        onClose: onAddToCartModalClose,
    } = useAddToCartModalContext()

    useEffect(() => {
        if (isAddToCartModalOpen) {
            onAddToCartModalClose()
        }
    }, [location.pathname])

    const variant = useVariant(product, false, false)

    useEffect(() => {
        if (!basket) return
        if (!variant?.productId) return

        const initConfiguration = async () => {
            //We need to pass the following data to the ThreekitWidget component along with the token
            const data = {
                organizationId: config.organizationId,
                basketId: basket.basketId,
                siteId: site.id,
                locale: locale.id,
            }

            const token = await getTokenWhenReady()

            const encodedData = btoa(token + '|' + JSON.stringify(data))
            window.DLG = {
                pdp: {},
                config: {
                    encodedContextPath: MRT_CONTEXT_BASEPATH,
                    CSRFToken: encodedData,
                },
                EVE: {
                    emit: (e, val) => {
                        if (e === 'CART.ADD.CONFIGURABLE.PRODUCT') {
                            onAddToCartModalOpen({
                                product,
                                itemsAdded: [{product, quantity: 1, variant}],
                                selectedQuantity: 1,
                                officinaShapshot:
                                    val?.productItems?.[0]?.c_officinaConfigurationImage,
                            })
                            refetch()
                        }
                    },
                },
                gigya: {brand: BRAND_KEY},
            }
            /*
            This is the data that we need to pass to the ThreekitWidget component
            */
            window.DLG.pdp.sku = variant?.productId
            window.DLG.pdp.formattedPrice = intl.formatNumber(price, {
                style: 'currency',
                currency,
            })
            window.DLG.config.currentLanguageIsocode = locale.id.slice(0, 2) || 'en'
            setInit(true)
        }

        initConfiguration()
    }, [basket, product])

    return (
        <>
            <Helmet>
                <link
                    rel="stylesheet"
                    href={`${getAssetUrl('static/vendors/threekit/dev/treble-app.css')}`}
                />
            </Helmet>
            {init && (
                <Box __css={styles}>
                    <Box __css={styles.closeButtonContainer}>
                        <IconButton
                            icon={<CloseIcon {...styles.closeIcon} />}
                            onClick={() => onOpen()}
                        />
                    </Box>
                    <ThreekitWidget />
                    <OfficinaModal isOpen={isOpen} onClose={onClose} onOpen={onOpen} />
                </Box>
            )}
        </>
    )
}

OfficinaPage.getTemplateName = () => 'officina-detail'

OfficinaPage.propTypes = {
    product: PropTypes.object,
}

export default OfficinaPage
